import React, { useEffect } from 'react';
import { isBlank } from 'utils/helperMethods';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import { Box, Typography, Button } from '@material-ui/core';
import MortgageSurveyQuestionPresenter from 'presenters/MortgageSurveyQuestionPresenter';
import InputWithSlider from 'sharedComponents/InputWithSlider';
import MortgageSurveyPresenter from 'presenters/MortgageSurveyPresenter';

import useStyles from './useStyles';

const CASH_OUT_OPTIONS = {
  zeroBalance: 'refinance_cash_out',
  noZeroBalance: 'refinance',
  unavailable: 'refinance_no_cash_out',
};

const MIN_DOWN_PAYMENT = 10000;
/** The expected mortgage balance percentage for typical refinance */
const AVERAGE_EXPECTED_MORTGAGE_BALANCE_RATIO = 70 / 100;
/** The maximum suggested downpayment for the price target */
const MAX_VALUE_RATIO_FROM_HOME_COST = 90 / 100;
/** The maximum suggested downpayment for the price target */
const MAX_VALUE_RATIO_FOR_CASHOUT = 80 / 100;

const MortgageBalanceQuestion = props => {
  const { question, onChange, surveyValues, onValidate, onNext, isValid } = props;
  const { t } = useTranslation();
  const classes = useStyles();

  const [
    mortgageBalanceField,
    downPaymentTargetField,
    purposeField,
    cashOutAmountField,
  ] = MortgageSurveyQuestionPresenter.surveyValueFields(question);
  const priceTarget = MortgageSurveyPresenter.priceTarget(surveyValues);
  const currentCashOutAmountValue = MortgageSurveyPresenter[cashOutAmountField](surveyValues);
  const currentValue = MortgageSurveyPresenter[mortgageBalanceField](surveyValues);
  const max = priceTarget * MAX_VALUE_RATIO_FROM_HOME_COST;

  useEffect(() => {
    const isValueValid = !isBlank(currentValue) && currentValue <= priceTarget && currentValue >= 0;
    onValidate(isValueValid);
  }, [currentValue]);

  useEffect(() => {
    onChange([{ field: 'mortgageBalance', value: priceTarget * AVERAGE_EXPECTED_MORTGAGE_BALANCE_RATIO }]);
  }, []);

  const options = MortgageSurveyQuestionPresenter.options(question);
  const { min, marksDivider } = options;

  const shouldShowWarning = currentValue > priceTarget || currentValue === 0;
  const warningText =
    currentValue > priceTarget
      ? t('components.MortgageSurvey.MortgageBalanceQuestion.higherWarning')
      : t('components.MortgageSurvey.MortgageBalanceQuestion.paidOffWarning');

  const handleValueChange = value => {
    const purposeByBalance =
      typeof value === 'number' && value === 0 ? CASH_OUT_OPTIONS.zeroBalance : CASH_OUT_OPTIONS.noZeroBalance;
    const downPaymentTargetValue = priceTarget - value;
    const isAvailableCashOut =
      downPaymentTargetValue >= MIN_DOWN_PAYMENT && value / priceTarget <= MAX_VALUE_RATIO_FOR_CASHOUT;
    const purposeValue = isAvailableCashOut ? purposeByBalance : CASH_OUT_OPTIONS.unavailable;

    const defaultCashOutAmount = MortgageSurveyPresenter.isCashOut(purposeValue) ? currentCashOutAmountValue : null;
    onChange([
      { field: mortgageBalanceField, value },
      { field: downPaymentTargetField, value: downPaymentTargetValue },
      { field: purposeField, value: purposeValue },
      { field: cashOutAmountField, value: defaultCashOutAmount },
    ]);
  };

  return (
    <Box width="100%">
      <Typography variant="h1">{MortgageSurveyQuestionPresenter.label(question)}</Typography>
      <Box mt={2} />
      <Typography variant="body1">{MortgageSurveyQuestionPresenter.description(question)}</Typography>
      <Box mt={4} />
      <InputWithSlider
        className={classes.input}
        value={currentValue}
        min={min}
        max={max}
        marksDivider={marksDivider}
        label={MortgageSurveyQuestionPresenter.inputLabel(question)}
        onChange={handleValueChange}
      />
      <Box width="100%" className={classes.buttonBox}>
        <Button onClick={onNext} className={classes.button} variant="contained" disabled={!isValid}>
          {t('components.MortgageSurvey.ControlBar.nextButton')}
        </Button>
      </Box>
      {shouldShowWarning && (
        <Box width="100%" className={classes.warning} mt={1} p={2}>
          <Typography variant="body1" color="inherit" className={classes.warningHeader}>
            {t('components.MortgageSurvey.MortgageBalanceQuestion.warning')}
          </Typography>
          <Box mt={2} />
          <Typography variant="body2">{warningText}</Typography>
        </Box>
      )}
    </Box>
  );
};

MortgageBalanceQuestion.propTypes = {
  question: MortgageSurveyQuestionPresenter.shape().isRequired,
  onChange: PropTypes.func.isRequired,
  surveyValues: MortgageSurveyPresenter.shape().isRequired,
  onValidate: PropTypes.func.isRequired,
};

export default MortgageBalanceQuestion;



// WEBPACK FOOTER //
// ./src/sharedComponents/MortgageSurvey/components/MortgageBalanceQuestion/MortgageBalanceQuestion.js